/**
 * Small sidebar chip showing the signed-in user's rolling 30-day AI spend.
 * Lives above the user profile tile in `Layout.jsx`.
 *
 * Data source: GET /api/video-gen/ai-spend/me → {cost_usd, entries, window_days}
 * Re-fetches every 60s + on window focus. Silent-fails (returns null) so
 * a backend hiccup never breaks the sidebar.
 */
import { useEffect, useState, useCallback } from "react";
import { Zap } from "lucide-react";
import { api } from "../../lib/api";

export default function SidebarAISpendChip({ sc }) {
  const [spend, setSpend] = useState(null);

  const fetchSpend = useCallback(async () => {
    try {
      const r = await api.get("/video-gen/ai-spend/me?days=30");
      setSpend(r.data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchSpend();
    const t = setInterval(fetchSpend, 60_000);
    const onFocus = () => fetchSpend();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchSpend]);

  if (!spend) return null;
  const usd = spend.cost_usd || 0;

  return (
    <div
      className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs mb-2"
      style={{
        background: `color-mix(in srgb, ${sc?.accent || "#7C3AED"} 8%, transparent)`,
        border: `1px solid color-mix(in srgb, ${sc?.accent || "#7C3AED"} 22%, transparent)`,
      }}
      data-testid="sidebar-ai-spend-chip"
      title={`${spend.entries} generations in the last ${spend.window_days} days`}
    >
      <span className="flex items-center gap-1.5" style={{ color: sc?.textMuted || "#a1a1aa" }}>
        <Zap className="w-3 h-3" />
        <span className="uppercase tracking-wider text-[9px] font-semibold">AI spend · 30d</span>
      </span>
      <span className="font-bold tabular-nums" style={{ color: sc?.text || "#fff" }} data-testid="sidebar-ai-spend-amount">
        USD ${usd.toFixed(2)}
      </span>
    </div>
  );
}
